import React from 'react'
import ReactModal, { Props } from 'react-modal'
import Draggable from 'react-draggable'
import { pathOr } from 'ramda'
import cx from 'classnames'
import styled from 'styled-components'
import { DisplayAlert } from './Video.components'
import Player from './Player'
import FlvPlayer from './FlvPlayer'
import MjpgPlayer from './MjpgPlayer'
import IconClose from './icons/close'
import IconFullscreen from './icons/fullscreen'
import IconPlay from './icons/play'
import { Button } from '@shared/innmaxUI/Form'
import { MediaType } from './Video.types'
import { Device, useLiveStreaming } from '@services/device'
import { Streaming } from '@services/device'
import { Spin } from 'antd'
import HTMLPlayer from './HTMLPlayer'
import message from '@shared/innmaxLib/utils/message'
import { useTranslation } from 'react-i18next'

export type CameraModalProps = {
  device: Device
  onRequestClose?: (d: Device) => void
  onClose?: (d: Device) => void
} & Props

export default function CameraModal({
  device,
  onRequestClose,
  onClose,
  ...props
}: CameraModalProps) {
  const { t } = useTranslation()

  const [isFullscreen, setIsFullscreen] = React.useState(false)

  const [isPlaying, setIsPlaying] = React.useState(false)
  const [streaming, setStreaming] = React.useState<Streaming | undefined>(
    undefined
  )

  const isIpCam = (stramingURL: string) =>
    /(http:\/\/|https:\/\/)?(?:[0-9]{1,3}\.){3}[0-9]{1,3}[/]?$/.test(
      stramingURL
    )

  const { queryLiveStreaming, isError, isLoading } = useLiveStreaming({
    onSuccess: (res: any) => {
      const streaming = res?.data as Streaming

      setStreaming(streaming)

      // 菲律賓 Demo 改為 IP cam 為跳窗
      if (
        streaming?.streamingType === MediaType.HTML &&
        isIpCam(streaming?.streamingUrl)
      ) {
        return window.open(streaming.streamingUrl)
      }
      if (
        streaming?.streamingType === MediaType.MJPG &&
        streaming?.streamingUrl.match(/(https|http).+:.+@.+/)
      ) {
        return window.open(streaming.streamingUrl)
      }
      setIsPlaying(true)
    },
  })

  const toggleFullScreen = () => {
    setIsFullscreen(!isFullscreen)
  }

  const handleRequestClose = (e: React.MouseEvent) => {
    setIsPlaying(false)
    if (onClose) return onClose(device)
    onRequestClose && onRequestClose(e)
  }

  const handlePlay = () => {
    if (device) {
      queryLiveStreaming({ id: device?.id }, false)
    } else {
      message({
        content: '攝影機資料異常',
        type: 'error',
      })
    }
  }

  const renderPlayer = () => {
    if (!isPlaying) {
      return (
        <Button
          type="icon"
          className="absolute z-50"
          onClick={handlePlay}
          style={{ width: 80, height: 80 }}>
          {isLoading ? (
            <Spin />
          ) : (
            <IconPlay style={{ width: 80, height: 80 }} />
          )}
        </Button>
      )
    }
    if (!streaming || !streaming.streamingUrl || isError) {
      return <DisplayAlert message="無法播放,無串流連結" />
    }
    if (streaming.streamingType === MediaType.FLV) {
      return <FlvPlayer isPlaying={isPlaying} url={streaming.streamingUrl} />
    }
    if (Number(streaming.streamingType) === MediaType.MP4) {
      return <Player url={streaming.streamingUrl} isPlaying={isPlaying} />
    }
    if (streaming.streamingType === MediaType.MJPG) {
      return <MjpgPlayer isPlaying={isPlaying} url={streaming.streamingUrl} />
    }
    if (streaming.streamingType === MediaType.HTML) {
      return <HTMLPlayer isPlaying={isPlaying} url={streaming.streamingUrl} />
    }
    return (
      <DisplayAlert
        message={t('device:the video play failed, the file type not support')}
      />
    )
  }

  return (
    <ReactModal
      {...props}
      shouldCloseOnOverlayClick={false}
      ariaHideApp={false}
      onRequestClose={handleRequestClose}
      style={{
        overlay: {
          zIndex: 1200,
          background: 'transparent',
          position: 'absolute',
          pointerEvents: 'none',
        },
        content: {
          padding: 0,
          inset: 0,
          background: 'transparent',
          border: 0,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...(isFullscreen && {
            width: '100%',
            height: '100%',
            borderRadius: 0,
          }),
        },
      }}>
      <Draggable
        cancel=".video-react-control-bar"
        handle=".modal-content"
        bounds="body">
        <ModalContent
          onClick={e => {
            e.preventDefault()
            e.stopPropagation()
          }}
          className={cx('modal-content', {
            'w-full h-full transform-none': isFullscreen,
          })}>
          <div className="flex justify-between items-center px-16 pt-16">
            <div className="text-white font-medium">
              <span className="text-[16px]">
                即時影像 {pathOr('', ['displayName'], device)}
              </span>
            </div>
            <div className="flex">
              <Button
                type="icon"
                title={isFullscreen ? '離開全螢幕' : '全螢幕'}
                onClick={toggleFullScreen}>
                <IconFullscreen style={{ width: 32, height: 32 }} />
              </Button>
              <Button
                type="icon"
                title="關閉"
                className="ml-8"
                onClick={handleRequestClose}>
                <IconClose style={{ width: 32, height: 32 }} />
              </Button>
            </div>
          </div>
          <div className="px-24 pt-14 pb-24">
            <div
              className="relative flex justify-center items-center"
              style={{ height: isFullscreen ? 'calc(100vh - 80px)' : 320 }}>
              {renderPlayer()}
            </div>
          </div>
        </ModalContent>
      </Draggable>
    </ReactModal>
  )
}

const ModalContent = styled.div`
  width: 608px;
  background: #222;
  box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  pointer-events: all;
  cursor: move;

  .video-react-big-play-button {
    display: none;
  }
`
